/**
 * Simple Speech Synthesis Utility
 * Supports English and Vietnamese with basic heuristic detection.
 */

export interface SpeechOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
  audio?: string; // Standardized: support direct audio file playback
}

// Shared audio element for playing URL-based audio files
let sharedAudioPlayer: HTMLAudioElement | null = null;
const getAudioPlayer = () => {
  if (typeof window === 'undefined') return null;
  if (!sharedAudioPlayer) {
    sharedAudioPlayer = new Audio();
  }
  return sharedAudioPlayer;
};

const VIETNAMESE_CHARS_REGEX = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;

// Warm up voices
if (typeof window !== 'undefined' && window.speechSynthesis) {
  window.speechSynthesis.getVoices();
}

export const detectLanguage = (text: string): 'vi-VN' | 'en-US' => {
  return VIETNAMESE_CHARS_REGEX.test(text) ? 'vi-VN' : 'en-US';
};

/**
 * Clean text for cleaner speech (remove parenthetical notes, etc.)
 * e.g., "Disease (n)" -> "Disease"
 */
export const cleanTextForSpeech = (text: string): string => {
  return text
    .replace(/\([a-z\s.,]*\)/gi, '') // Remove (n), (v), (adj), etc.
    // .replace(/;.*/g, '')          // Uncomment if you want to skip multiple meanings
    .trim();
};

// Shared audio element to bypass some browser loading restrictions
let sharedAudio: HTMLAudioElement | null = null;

export const speak = (text: string, options: SpeechOptions = {}) => {
  if (typeof window === 'undefined') return;

  // 1. If audio is provided, PLAY THE FILE (Priority 1)
  if (options.audio) {
    const player = getAudioPlayer();
    if (player) {
      console.log(`[TTS] Playing custom audio: ${options.audio}`);
      player.src = options.audio;
      player.volume = options.volume || 1.0;
      player.play().catch(err => {
        console.error('[TTS] Audio file playback failed:', err);
        // Fallback to TTS if file playback fails
        synthesizeText(text, options);
      });
      return;
    }
  }

  // 2. Otherwise, use SpeechSynthesis (Priority 2)
  synthesizeText(text, options);
};

export const getAvailableVoices = () => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return [];
  return window.speechSynthesis.getVoices();
};

const synthesizeText = (text: string, options: SpeechOptions) => {
  if (!window.speechSynthesis) return;

  const cleanedText = cleanTextForSpeech(text);
  if (!cleanedText) return;

  const targetLang = options.lang || detectLanguage(text);

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(cleanedText);
  utterance.lang = targetLang;
  utterance.rate = options.rate || 1.0; 
  utterance.pitch = options.pitch || 1.0;
  utterance.volume = options.volume || 1.0;

  const voices = window.speechSynthesis.getVoices();
  
  const findBestVoice = (lang: string) => {
    const normalizedLang = lang.toLowerCase().replace('_', '-');
    const isMatch = (v: SpeechSynthesisVoice) => 
      v.lang.toLowerCase().replace('_', '-') === normalizedLang || 
      v.lang.toLowerCase().startsWith(normalizedLang.split('-')[0]);

    // Priority: Neural/Natural > Google > Any match
    let best = voices.find(v => isMatch(v) && (v.name.includes('Neural') || v.name.includes('Natural')));
    if (best) return best;
    best = voices.find(v => isMatch(v) && v.name.includes('Google'));
    if (best) return best;
    return voices.find(v => isMatch(v));
  };

  const selectedVoice = findBestVoice(targetLang);
  if (selectedVoice) {
    console.log(`[TTS] ✅ Using voice: ${selectedVoice.name} (${selectedVoice.lang})`);
    utterance.voice = selectedVoice;
  }

  window.speechSynthesis.speak(utterance);
};

export const stopSpeech = () => {
  if (typeof window !== 'undefined') {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (sharedAudioPlayer) {
      sharedAudioPlayer.pause();
      sharedAudioPlayer.currentTime = 0;
    }
  }
};
